import * as React from 'react';
import {
  SetterOrUpdater,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import { useEffect, useRef, useState } from 'react';
import { IRide, IRidePosition, TRideActiveLayer } from '../types';
import {
  activeLastRideIdAtom,
  activeRideNumberAtom,
  actualPlayPositionAtom,
  autoplayAtom,
  isRidePlayedAtom,
  nextAtom,
  pauseAtom,
  playAtom,
  playerSpeedAtom,
  prevAtom,
  rideAutofocusAtom,
  stopAtom,
} from '../RidePlayerState';
import {
  isRidePlayerOpenAtom,
  lastRideForSelectedVehicleAtom,
  vehicleFocusAtom,
} from '../../OnlineMapNew/OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IUsePlayer {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stop: boolean;
  setStop: SetterOrUpdater<boolean>;
  pause: boolean;
  setPause: React.Dispatch<React.SetStateAction<boolean>>;
  play: boolean;
  setPlay: SetterOrUpdater<boolean>;
  prev: boolean;
  setPrev: SetterOrUpdater<boolean>;
  next: boolean;
  setNext: SetterOrUpdater<boolean>;
  time: number;
  setTime: React.Dispatch<React.SetStateAction<number>>;
  speed: number;
  setSpeed: React.Dispatch<React.SetStateAction<number>>;
  playerSpeed: number;
  autoplay: boolean;
  setAutoplay: SetterOrUpdater<boolean>;
  firstPlay: boolean;
  setFirstPlay: React.Dispatch<React.SetStateAction<boolean>>;
  enableSpeedLimits: boolean;
  setEnableSpeedLimits: React.Dispatch<React.SetStateAction<boolean>>;
  interval: number;
  setInterval: React.Dispatch<React.SetStateAction<number>>;
  handleToggleSize: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  handleIncreaseSpeed: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  handleDecreaseSpeed: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  handleToggleEnableSpeedLimits: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  activeRide: React.MutableRefObject<number>;
  zoomLevel: number;
  handleSpeedMenu: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  isSpeedMenuOpen: boolean;
  setIsSpeedMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeLayer: TRideActiveLayer;
  handleActiveLayer: (layer: TRideActiveLayer) => void;
  // rideForVehicle: number | null;
}

/**
 * usePlayer custom hook
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @returns {IUsePlayer}
 */
const usePlayer = (
  map: google.maps.Map,
  rides: IRide[],
  isLastRidePlayer?: boolean,
  newPosition?: IRidePosition | null
): IUsePlayer => {
  // const [rideForVehicle, setRideForVehicle] = React.useState<number | null>(null);
  const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const [stop, setStop] = useRecoilState<boolean>(stopAtom);
  const [pause, setPause] = useRecoilState<boolean>(pauseAtom);
  const [play, setPlay] = useRecoilState<boolean>(playAtom);
  const [prev, setPrev] = useRecoilState<boolean>(prevAtom);
  const [next, setNext] = useRecoilState<boolean>(nextAtom);
  const [time, setTime] = useState<number>(0);
  const [speed, setSpeed] = useState<number>(0);
  const [playerSpeed, setPlayerSpeed] = useRecoilState<number>(playerSpeedAtom);
  const [autoplay, setAutoplay] = useRecoilState<boolean>(autoplayAtom);
  const [firstPlay, setFirstPlay] = useState<boolean>(true);
  const [enableSpeedLimits, setEnableSpeedLimits] = useState<boolean>(true);
  const [interval, setInterval] = useState<number>(1000 / playerSpeed);
  const setActualPosition = useSetRecoilState<number>(actualPlayPositionAtom);
  const setActiveRideNumber = useSetRecoilState(activeRideNumberAtom);
  const [zoomLevel, setZoomLevel] = useState<number>(map.getZoom() || 10);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState<boolean>(false);
  const [zoomChangedListenerId, setZoomChangedListenerId] =
    useState<google.maps.MapsEventListener | null>(null);
  const resetActiveLastRideId = useResetRecoilState(activeLastRideIdAtom);
  const [activeLayer, setActiveLayer] = useState<TRideActiveLayer>('ride');
  const setRideAutofocus = useSetRecoilState<boolean>(rideAutofocusAtom);
  const isRidePlayed = useRecoilValue<boolean>(isRidePlayedAtom);
  const activeRide = useRef<number>(1);
  // const [rideForVehicle, setRideForVehicle] = useState<number | null>(null);
  const isRidePlayerOpen = useRecoilValue<number | boolean>(isRidePlayerOpenAtom);

  const setVehicleFocus = useSetRecoilState<boolean>(vehicleFocusAtom);
  const setLastRideForSelectedVehicle = useSetRecoilState<IRide | null>(
    lastRideForSelectedVehicleAtom
  );

  // Set active ride number to use in panel.
  // @todo why not use activeRideNumber from recoil everywhere?
  useEffect(() => {
    if (activeRide.current) {
      setActiveRideNumber(activeRide.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setActiveRideNumber, activeRide.current]);

  // When player is mounted.
  useEffect(() => {
    // Listen for mousewheel events to disable ride autofocus
    window.addEventListener('mousewheel', () => {
      setRideAutofocus(false);
    });
    // Listen for zoom changes on google map
    const zoomChangedListener = map.addListener('zoom_changed', () => {
      const mapZoom = map.getZoom();
      if (mapZoom) {
        setZoomLevel(mapZoom);
      }
    });
    setZoomChangedListenerId(zoomChangedListener);

    // if (vehicleId) {
    //   setRideForVehicle(vehicleId);
    // }

    setVehicleFocus(false);
    setRideAutofocus(true);

    setAutoplay(!isLastRidePlayer);
    if (isLastRidePlayer) {
      setActualPosition(0);
    } else {
      const newActualPosition = rides[activeRide.current - 1].positions.length;
      setActualPosition(newActualPosition);
    }

    // When player is unmmounted
    return () => {
      // Reset recoil state after unmount
      if (isRidePlayerOpen) {
        setPlay(false);
        setPause(false);
        setStop(false);
        resetActiveLastRideId();
        setRideAutofocus(false);
        setActualPosition(0);
        // Remove listener
        if (zoomChangedListenerId) {
          google.maps.event.removeListener(zoomChangedListenerId);
        }
        window.removeEventListener('mousewheel', () => {
          return null;
        });
        // setRideForVehicle(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add new position to rides
  useEffect(() => {
    if (newPosition && rides[activeRide.current - 1]) {
      const lastPosition =
        rides[activeRide.current - 1].positions[rides[activeRide.current - 1].positions.length - 1];
      if (lastPosition.id !== newPosition.id) {
        setLastRideForSelectedVehicle((oldState: IRide | null) => {
          if (oldState) {
            const newPositions = [...oldState.positions, newPosition];
            const newState: IRide = {
              ...oldState,
              positions: newPositions,
            };
            return newState;
          }
          return oldState;
        });

        if (!isRidePlayed) {
          const newActualPosition = rides[activeRide.current - 1].positions.length - 1;
          setActualPosition(newActualPosition);
        }
      }
    }
  }, [isRidePlayed, newPosition, rides, setActualPosition, setLastRideForSelectedVehicle]);

  // When player speed changes, change also interval for play loop
  useEffect(() => {
    setInterval(1000 / playerSpeed);
  }, [playerSpeed]);

  /**
   * Toggle open/hide player
   * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} event
   * @returns {void}
   */
  const handleToggleSize = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    if (isOpen) {
      trackEvent(['Ride player', 'click', 'Minimize player']);
      setIsOpen(false);
    } else {
      trackEvent(['Ride player', 'click', 'Maximize player']);
      setIsOpen(true);
    }
    setIsOpen(!isOpen);
  };

  /**
   * Toggle open/hide speed menu
   * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} event
   * @returns {void}
   */
  const handleSpeedMenu = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    if (isSpeedMenuOpen) {
      trackEvent(['Ride player', 'click', 'Close speed menu']);
      setIsSpeedMenuOpen(false);
    } else {
      trackEvent(['Ride player', 'click', 'Open speed menu']);
      setIsSpeedMenuOpen(true);
    }
    setIsSpeedMenuOpen(!isSpeedMenuOpen);
  };

  /**
   * Increase speed handler
   * @param {React.MouseEvent<HTMLLIElement, MouseEvent>} event
   * @returns {void}
   */
  const handleIncreaseSpeed = (event: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
    event.preventDefault();
    const increasePlayerSpeed = playerSpeed * 2;
    if (playerSpeed < 64) {
      setPlayerSpeed(increasePlayerSpeed);
      setInterval(1000 / increasePlayerSpeed);
    }
  };

  /**
   * Decrease speed handler
   * @param {React.MouseEvent<HTMLLIElement, MouseEvent>} event
   * @returns {void}
   */
  const handleDecreaseSpeed = (event: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
    event.preventDefault();
    const decreasePlayerSpeed = playerSpeed / 2;
    if (decreasePlayerSpeed >= 1) {
      setPlayerSpeed(decreasePlayerSpeed);

      setInterval(1000 / decreasePlayerSpeed);
    }
  };

  /**
   * Toggle speed limits
   * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} event
   * @returns {void}
   */
  const handleToggleEnableSpeedLimits = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    event.preventDefault();
    setEnableSpeedLimits(!enableSpeedLimits);
  };

  /**
   * Switch active layer
   * @param {TRideActiveLayer} layer
   * @returns {void}
   */
  const handleActiveLayer = (layer: TRideActiveLayer) => {
    setActiveLayer(layer);
  };

  // When player is in passive mode, receiving only actual position and without being played,
  // set actual position to last position of a ride
  useEffect(() => {
    if (!play && !stop && !pause && !isRidePlayed && isLastRidePlayer) {
      const lastRidePositionIndex = rides[activeRide.current - 1].positions.length - 1;
      setActualPosition(lastRidePositionIndex);
    }
  }, [isLastRidePlayer, isRidePlayed, pause, play, rides, setActualPosition, stop]);

  const data: IUsePlayer = {
    isOpen,
    setIsOpen,
    stop,
    setStop,
    pause,
    setPause,
    play,
    setPlay,
    prev,
    setPrev,
    next,
    setNext,
    time,
    setTime,
    speed,
    setSpeed,
    playerSpeed,
    autoplay,
    setAutoplay,
    firstPlay,
    setFirstPlay,
    enableSpeedLimits,
    setEnableSpeedLimits,
    interval,
    setInterval,
    handleToggleSize,
    handleIncreaseSpeed,
    handleDecreaseSpeed,
    handleToggleEnableSpeedLimits,
    activeRide,
    zoomLevel,
    handleSpeedMenu,
    isSpeedMenuOpen,
    activeLayer,
    handleActiveLayer,
    // rideForVehicle,
    setIsSpeedMenuOpen,
  };
  return data;
};

export default usePlayer;
